import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILTERED_GREEN_BUTTON } from '../constants/colors';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';

interface UnreadFounderMessageCalloutProps {
  onDismiss: () => void;
  onViewMessages: () => void;
}

const TAIL_SIZE = 14;

function calloutPosition(): { left: number; top: number; tipLeft: number } {
  const width = Math.min(360, window.innerWidth - 32);
  const maxLeft = Math.max(16, window.innerWidth - width - 16);
  const anchors = Array.from(document.querySelectorAll<HTMLElement>('[data-messages-nav]'));
  // Prefer the visible Messages control (desktop lg+ vs compact/mobile icon).
  const anchor =
    anchors.find(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) || null;

  if (anchor) {
    const rect = anchor.getBoundingClientRect();
    const tipX = rect.left + rect.width / 2;
    const left = Math.min(Math.max(16, tipX - width * 0.72), maxLeft);
    return {
      left,
      top: rect.bottom + 10,
      tipLeft: Math.min(Math.max(18, tipX - left - TAIL_SIZE / 2), width - 28),
    };
  }

  return { left: 24, top: 72, tipLeft: 24 };
}

/**
 * Points at header Messages when an investor has ignored founder unread across 3 sessions.
 */
const UnreadFounderMessageCallout: React.FC<UnreadFounderMessageCalloutProps> = ({
  onDismiss,
  onViewMessages,
}) => {
  const [position, setPosition] = useState(calloutPosition);

  useEffect(() => {
    const update = () => setPosition(calloutPosition());
    update();
    const frame = window.requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100000]" onClick={onDismiss} aria-hidden />
      <div
        role="dialog"
        aria-label="Unread message from a founder"
        className="fixed z-[100001] w-[min(22.5rem,calc(100vw-2rem))]"
        style={{
          left: position.left,
          top: position.top,
        }}
        onClick={event => event.stopPropagation()}
      >
        <div
          className="relative rounded-2xl bg-white px-4 py-3 shadow-xl"
          style={{ color: PRAIRI_SURFACES.page, fontFamily: 'Rubik, sans-serif' }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute bg-white"
            style={{
              left: position.tipLeft,
              top: -TAIL_SIZE + 2,
              width: TAIL_SIZE,
              height: TAIL_SIZE,
              transform: 'rotate(45deg)',
              boxShadow: '-1px -1px 2px rgba(0,0,0,0.04)',
            }}
          />
          <div className="relative">
            <p className="text-[15px] leading-snug">You have an unread message from a founder.</p>
            <div className="mt-2.5 flex justify-end">
              <button
                type="button"
                onClick={onViewMessages}
                className={`rounded-full px-4 py-1 text-sm font-medium ${FILTERED_GREEN_BUTTON}`}
              >
                View messages
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default UnreadFounderMessageCallout;
